/*
  dp-menu.js — the header menu drawer.

  Replaces Dawn's MenuDrawer + HeaderDrawer (~180 lines in global.js), which were
  deleted: nothing else in the theme rendered <menu-drawer> once filtering and
  sorting were removed. See MENU-DRAWER-HOOKS.md §0 and §10.

  Everything the accordion does is native <details>. This element owns only what
  markup cannot express:
    - the slide (a <details> renders nothing until open, so there is no start
      state to transition from — `is-open` lands on the `toggle` event)
    - the scroll lock, focus trap, Escape, and closing on a scrim click
    - swipe-left to close (touch and pen; a mouse has the scrim and the X)
    - the language dropup's expand/collapse

  One listener per event type, all delegated from this element and routed through
  handleEvent(), all under one AbortController — so a theme-editor re-render
  (disconnect + connect) never stacks a second set of handlers.

  Escape ownership matters: global.js:71 deliberately skips its per-summary Escape
  handler for anything inside header-drawer, because the drawer's element owns the
  key. If this stops handling Escape, nothing else will.

  Tested by tools/visual/behavior.spec.mjs ('menu drawer'): bun run behavior:dev.
*/

// swipe: a drag past this share of the panel's width closes it, as does a flick
// faster than FLICK px/ms. SLOP px of travel before the gesture picks an axis.
// A finger that rested longer than FLICK_GAP ms before lifting is not flicking.
const CLOSE_SHARE = 0.35;
const FLICK = 0.5;
const FLICK_GAP = 100;
const SLOP = 8;

class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    this.panel = this.querySelector('.dp-menu__panel');
    this.summary = this.details?.querySelector('summary');
    // guard BEFORE any of them is used: connectedCallback also runs when the
    // theme editor re-renders the section, and a half-built subtree here would
    // throw past this file and take the rest of the page's JS with it
    if (!this.details || !this.summary || !this.panel) return;
    this.langButton = this.querySelector('.dp-menu__lang-btn');
    this.langMenu = this.querySelector('.dp-menu__lang-menu');

    this.events = new AbortController();
    const options = { signal: this.events.signal };
    // capture, so a click that ends a swipe is swallowed before a link sees it
    this.addEventListener('click', this, { ...options, capture: true });
    this.addEventListener('keydown', this, options);
    this.details.addEventListener('toggle', this, options);
    // on <details>, not the panel: the scrim is its ::before, and a swipe that
    // starts on the scrim is the one people reach for first
    for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
      this.details.addEventListener(type, this, options);
    }
    this.panel.addEventListener('transitionend', this, options);
  }

  disconnectedCallback() {
    this.events?.abort();
    clearTimeout(this.closeTimer);
    if (!this.details?.hasAttribute('open')) return;
    // removed while open (a move, or the editor swapping the section): reset to
    // closed. A reconnect only re-adds listeners and no `toggle` fires, so an
    // element left open would come back visible with no scroll lock or focus trap.
    if (this.details.classList.contains('is-open')) {
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      this.setPageInert(false);
    }
    this.endDrag();
    this.closeLang();
    this.details.classList.remove('is-open');
    this.details.removeAttribute('open');
    this.setExpanded(false);
  }

  handleEvent(event) {
    switch (event.type) {
      case 'click': return this.onClick(event);
      case 'keydown': return this.onKeyDown(event);
      case 'toggle': return this.onToggle();
      case 'pointerdown': return this.onPointerDown(event);
      case 'pointermove': return this.onPointerMove(event);
      case 'pointerup':
      case 'pointercancel': return this.onPointerUp(event);
      case 'transitionend': return this.onTransitionEnd(event);
    }
  }

  get isOpen() {
    return this.details.hasAttribute('open');
  }

  onClick(event) {
    if (this.swiped) {
      // the click a touch-drag can still produce on release
      this.swiped = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const target = event.target;
    // the language dropup closes on any click outside it — the scrim covers the
    // whole page while the drawer is open, so every such click lands in here
    if (this.langMenu && !this.langMenu.hidden && !target.closest('.dp-menu__lang')) this.closeLang();

    if (target === this.details || target.closest('.dp-menu__close')) {
      // the scrim is [open]::before, so its clicks are attributed to <details>
      this.close();
    } else if (target.closest('summary') === this.summary) {
      // <summary> toggles `open` itself. Opening is left to it and picked up by
      // the `toggle` event; closing is intercepted so the panel can slide out
      // before `open` is removed.
      if (!this.isOpen) return;
      event.preventDefault();
      this.close();
    } else if (this.langButton && target.closest('.dp-menu__lang-btn')) {
      this.langMenu.hidden ? this.openLang() : this.closeLang();
    }
  }

  onKeyDown(event) {
    if (event.key === 'Tab' && this.isOpen) return this.wrapTab(event);
    if (event.key !== 'Escape' || !this.isOpen) return;
    // the innermost open thing takes each Escape: dropup, then accordion, then drawer
    const openGroup = event.target.closest?.('.dp-menu__group[open]');
    if (this.langMenu && !this.langMenu.hidden) {
      this.closeLang();
      this.langButton.focus();
    } else if (openGroup) {
      openGroup.removeAttribute('open');
      openGroup.querySelector('summary')?.focus();
    } else {
      this.close();
    }
  }

  // Native `toggle`, not a timer: it fires AFTER `open` lands, which is the first
  // moment the panel is rendered and therefore the first moment it has a start
  // state to transition from. The obvious alternatives are both worse — Dawn's
  // setTimeout is a guess at frame timing, and requestAnimationFrame does not run
  // at all in a backgrounded tab, which opens the drawer invisibly.
  onToggle() {
    if (!this.isOpen || this.details.classList.contains('is-open')) return;
    // read forces the translateX(-100%) start state to be computed before the
    // class flips it; without it the browser coalesces both into one style pass
    // and there is nothing to animate
    this.panel.getBoundingClientRect();
    this.details.classList.add('is-open');
    this.setExpanded(true);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    this.setPageInert(true);
    this.panel.focus();
  }

  // Tab wraps inside the panel. Not global.js trapFocus: that caches the first and last
  // focusable once, and the last one can be a button in the closed (hidden) language
  // dropup, so Tab from the dropup toggle left the drawer when the social links were off.
  // Recomputed on each press from what is actually rendered.
  wrapTab(event) {
    const focusable = getFocusableElements(this.panel).filter((el) =>
      el.checkVisibility ? el.checkVisibility() : el.offsetParent !== null
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === this.panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // aria-modal alone doesn't stop a screen reader's swipe/virtual cursor from walking
  // out of the panel into the page behind the scrim; inert does. Every <body> child
  // except the one holding the drawer (the header section), restoring only what this
  // set, so an element that was already inert stays inert.
  setPageInert(state) {
    if (state) {
      this.inerted = [...document.body.children].filter((el) => !el.contains(this) && !el.inert);
      this.inerted.forEach((el) => (el.inert = true));
    } else {
      this.inerted?.forEach((el) => (el.inert = false));
      this.inerted = null;
    }
  }

  // aria-expanded is owned here, not left to the global.js summary-click handler:
  // that one only fires when the SUMMARY is clicked, so closing by the X button,
  // Escape, a swipe or the scrim left it stuck on "true" — a lie to a screen
  // reader, and it also kept base.css's hamburger ::before scrim painted.
  setExpanded(state) {
    this.summary.setAttribute('aria-expanded', String(state));
  }

  close() {
    if (!this.isOpen) return;
    this.closeLang();
    // dropped in the same style pass as `is-open`, so a swipe-close slides on from
    // wherever the finger let go instead of jumping back first
    this.endDrag();
    this.details.classList.remove('is-open');
    this.setExpanded(false);
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    this.setPageInert(false);
    // focus goes back to the opener without dragging the viewport with it — a
    // bare .focus() on a header control scrolls a scrolled page to the top
    this.summary.focus({ preventScroll: true });

    // hold `open` until the slide-out finishes (onTransitionEnd). The floor covers
    // the cases where no transitionend arrives: reduced motion collapses the
    // transition to 0.01ms, and a backgrounded tab never fires it.
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.dropOpen(), 600);
  }

  onTransitionEnd(event) {
    // transitionend BUBBLES, and the panel is full of children that transition
    // `background` on hover. Closing by the X leaves the pointer on it, so its
    // hover unwinds as the panel slides away and fires ~200ms in — dropping `open`
    // mid-slide made the drawer vanish instead of leave. Only our own transform.
    if (event.target === this.panel && event.propertyName === 'transform') this.dropOpen();
  }

  dropOpen() {
    clearTimeout(this.closeTimer);
    if (!this.details.classList.contains('is-open')) this.details.removeAttribute('open');
  }

  // ---- swipe to close -------------------------------------------------------
  // Panel and scrim have `touch-action: pan-y` (dp-menu.css): the browser keeps
  // vertical scrolling and hands horizontal movement to us. A vertical start
  // therefore ends in pointercancel, which we treat as "not a swipe".

  onPointerDown(event) {
    if (event.pointerType === 'mouse' || !event.isPrimary) return;
    if (!this.details.classList.contains('is-open')) return;
    // the closed-state summary (burger) sits under the scrim, never a swipe start
    if (event.target.closest('.dp-menu__lang-menu, .dp-menu__toggle')) return;
    this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, axis: null, t: event.timeStamp, v: 0 };
  }

  onPointerMove(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.x;
    if (!drag.axis) {
      const dy = event.clientY - drag.y;
      if (Math.hypot(dx, dy) < SLOP) return;
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (drag.axis === 'y') return this.endDrag();
      this.details.setPointerCapture(event.pointerId);
      this.details.classList.add('is-dragging');
      this.width = this.panel.offsetWidth;
    }
    const next = Math.min(0, dx);
    const dt = event.timeStamp - drag.t;
    if (dt > 0) drag.v = (next - drag.dx) / dt;
    drag.dx = next;
    drag.t = event.timeStamp;
    // a fraction, so the CSS can move the panel (×100%) and fade the scrim from it
    this.details.style.setProperty('--dp-menu-drag', String(next / this.width));
  }

  onPointerUp(event) {
    const drag = this.drag;
    if (!drag || event.pointerId !== drag.id) return;
    if (drag.axis !== 'x') return this.endDrag();
    this.swiped = true;
    // a click (if any) follows pointerup synchronously; clear the flag after it
    setTimeout(() => (this.swiped = false));
    const flung =
      event.type === 'pointerup' &&
      event.timeStamp - drag.t < FLICK_GAP &&
      drag.v < -FLICK &&
      drag.dx < -SLOP;
    if (flung || drag.dx < -this.width * CLOSE_SHARE) this.close();
    else this.endDrag(); // snaps back: the transition runs from the dragged offset
  }

  endDrag() {
    this.drag = null;
    this.details.classList.remove('is-dragging');
    this.details.style.removeProperty('--dp-menu-drag');
  }

  // ---- language dropup ------------------------------------------------------

  openLang() {
    this.langMenu.hidden = false;
    this.langButton.setAttribute('aria-expanded', 'true');
  }

  closeLang() {
    if (!this.langMenu || this.langMenu.hidden) return;
    this.langMenu.hidden = true;
    this.langButton.setAttribute('aria-expanded', 'false');
  }
}

customElements.define('header-drawer', HeaderDrawer);
